import { CodeResponse, useGoogleLogin } from "@react-oauth/google";
import { useCommunication } from "./CommunicationWrapper";
import { PublicClientApplication } from "@azure/msal-browser";
import { useGoogleCalendarSync } from "./calendarSyncGoogle";
import { useMicrosoftCalendarSync } from "./calendarSyncMicrosoft";
import { CLOUDFLARE_URL } from "./config";

const msalConfig = {
    auth: {
        clientId: import.meta.env.NEXT_PUBLIC_AZURE_CLIENT_ID || "",
        authority: `https://login.microsoftonline.com/common/oauth2/v2.0/authorize`,
        redirectUri: window.location.origin
    },
};

const loginRequest = {
    scopes: ["Calendars.ReadWrite", "User.Read"],
};

export const useCalendarSync = () => {
    const { authenticatedFetch, callExtensionFunc, sendMessageAndListen, getOptions, updateOptions } = useCommunication();

    const { handleInitialGoogleSync, ensureBetterCampusGoogleCalendar, disconnectGoogleCalendar, resetGoogleCalendar, listGoogleCalendars } = useGoogleCalendarSync();
    const { ensureBetterCampusMicrosoftCalendar, handleInitialMicrosoftSync, disconnectMicrosoftCalendar, resetMicrosoftCalendar, listMicrosoftCalendars } = useMicrosoftCalendarSync();
    
    const handleGoogleCallback = async (codeResponse: CodeResponse) => {
        console.log("handleGoogleCallback", codeResponse, authenticatedFetch);
        const response = await authenticatedFetch(`${CLOUDFLARE_URL}/google-cal`, {
            method: "POST",
            body: {
                code: codeResponse.code
            }
        });

        const { message, error } = await response.json();

        if (!error && message) {
            updateOptions({ google_cal_sync_token: message, microsoft_cal_sync_token: null });

        } else {
            console.error("Error exchanging code for token:", error);
        }
    }

    const handleGoogleLogin = useGoogleLogin({
        onSuccess: handleGoogleCallback,
        flow: "auth-code",
        scope: "https://www.googleapis.com/auth/calendar",
    });

    const handleMicrosoftLogin = async () => {
        try {
            const msalInstance = new PublicClientApplication(msalConfig);
            await msalInstance.initialize();

            const loginResponse = await msalInstance.loginPopup(loginRequest);

            console.log("loginResponse", loginResponse);

            if (loginResponse && loginResponse.accessToken) {
                updateOptions({
                    microsoft_cal_sync_token: {
                        "access_token": loginResponse.accessToken,
                        "expires_at": new Date(loginResponse.expiresOn).getTime(),
                        "refresh_token": loginResponse.refreshToken
                    },
                    google_cal_sync_token: null
                });
            }
        } catch (error) {
            console.error("Microsoft login error:", error);
        }
    };

    const handleInitialSync = async () => {
        const options = getOptions("sync");
        if (options?.google_cal_sync_token) {
            await handleInitialGoogleSync();
        } else if (options?.microsoft_cal_sync_token) {
            await handleInitialMicrosoftSync();
        }
    }

    const ensureBetterCampusCalendar = async () => {
        const options = getOptions("sync");
        if (options?.google_cal_sync_token) {
            await ensureBetterCampusGoogleCalendar();
        } else if (options?.microsoft_cal_sync_token) {
            await ensureBetterCampusMicrosoftCalendar();
        }
    }

    const handleResetCalendar = async () => {
        const options = getOptions("sync");
        if (options?.google_cal_sync_token) {
            await resetGoogleCalendar();
        } else if (options?.microsoft_cal_sync_token) {
            await resetMicrosoftCalendar();
        }
    }

    const handleDisconnectCalendar = async () => {
        const options = getOptions("sync");
        if (options?.google_cal_sync_token) {
            await disconnectGoogleCalendar();
        } else if (options?.microsoft_cal_sync_token) {
            await disconnectMicrosoftCalendar();
        }
    }

    const listCalendars = async () => {
        const options = getOptions("sync");
        if (options?.google_cal_sync_token) {
            await listGoogleCalendars();
        } else if (options?.microsoft_cal_sync_token) {
            await listMicrosoftCalendars();
        }
    }

    return {
        ensureBetterCampusCalendar,
        handleDisconnectCalendar,
        handleResetCalendar,
        handleGoogleLogin,
        handleMicrosoftLogin,
        handleInitialSync,
        listCalendars,
    };
}